/**
 * 类型判断工具
 * @description 提供各种类型判断函数
 */

const toString = Object.prototype.toString;

/**
 * 获取值的类型标签
 * @param value 值
 * @returns 类型标签
 */
export const is = (value: unknown, type: string): boolean => {
  return toString.call(value) === `[object ${type}]`;
};

/**
 * 判断值是否已定义（非 undefined）
 * @param val 值
 */
export const isDef = <T = unknown>(val?: T): val is T => {
  return typeof val !== 'undefined';
};

/**
 * 判断值是否未定义
 * @param val 值
 */
export const isUnDef = <T = unknown>(val?: T): val is T => {
  return !isDef(val);
};

/**
 * 判断值是否为对象
 * @param val 值
 */
export const isObject = (val: unknown): val is Record<any, any> => {
  return val !== null && is(val, 'Object');
};

/**
 * 判断值是否为空
 * @param val 值
 */
export const isEmpty = <T = unknown>(val: T): val is T => {
  if (isArray(val) || isString(val)) {
    return val.length === 0;
  }

  if (val instanceof Map || val instanceof Set) {
    return val.size === 0;
  }

  if (isObject(val)) {
    return Object.keys(val).length === 0;
  }

  return false;
};

/**
 * 判断值是否为日期
 * @param val 值
 */
export const isDate = (val: unknown): val is Date => {
  return is(val, 'Date');
};

/**
 * 判断值是否为 null
 * @param val 值
 */
export const isNull = (val: unknown): val is null => {
  return val === null;
};

/**
 * 判断值是否为 null 或 undefined
 * @param val 值
 */
export const isNullAndUnDef = (val: unknown): val is null | undefined => {
  return isUnDef(val) && isNull(val);
};

/**
 * 判断值是否为 null 或 undefined（任一）
 * @param val 值
 */
export const isNullOrUnDef = (val: unknown): val is null | undefined => {
  return isUnDef(val) || isNull(val);
};

/**
 * 判断值是否为数字
 * @param val 值
 */
export const isNumber = (val: unknown): val is number => {
  return is(val, 'Number');
};

/**
 * 判断值是否为 Promise
 * @param val 值
 */
export const isPromise = <T = any>(val: unknown): val is Promise<T> => {
  return is(val, 'Promise') && isObject(val) && isFunction(val.then) && isFunction(val.catch);
};

/**
 * 判断值是否为字符串
 * @param val 值
 */
export const isString = (val: unknown): val is string => {
  return is(val, 'String');
};

/**
 * 判断值是否为函数
 * @param val 值
 */
export const isFunction = (val: unknown): val is (...args: any[]) => any => {
  return typeof val === 'function';
};

/**
 * 判断值是否为布尔值
 * @param val 值
 */
export const isBoolean = (val: unknown): val is boolean => {
  return is(val, 'Boolean');
};

/**
 * 判断值是否为正则表达式
 * @param val 值
 */
export const isRegExp = (val: unknown): val is RegExp => {
  return is(val, 'RegExp');
};

/**
 * 判断值是否为数组
 * @param val 值
 */
export const isArray = (val: unknown): val is Array<any> => {
  return val && Array.isArray(val);
};

/**
 * 判断值是否为 Window 对象
 * @param val 值
 */
export const isWindow = (val: unknown): val is Window => {
  return typeof window !== 'undefined' && is(val, 'Window');
};

/**
 * 判断值是否为 DOM 元素
 * @param val 值
 */
export const isElement = (val: unknown): val is Element => {
  return isObject(val) && !!val.tagName;
};

/**
 * 判断值是否为 Map
 * @param val 值
 */
export const isMap = (val: unknown): val is Map<any, any> => {
  return is(val, 'Map');
};

/**
 * 判断是否为服务端运行
 */
export const isServer = typeof window === 'undefined';

/**
 * 判断是否为客户端运行
 */
export const isClient = !isServer;

/**
 * 判断字符串是否为 URL
 * @param url 字符串
 */
export const isUrl = (url: string): boolean => {
  const reg =
    /^(((ht|f)tps?):\/\/)?([^!@#$%^&*?.\s-]([^!@#$%^&*?.\s]{0,63}[^!@#$%^&*?.\s])?\.)+[a-z]{2,6}\/?/;
  return reg.test(url);
};

/**
 * 判断字符串是否为手机号
 * @param phone 字符串
 */
export const isPhone = (phone: string): boolean => {
  const reg =
    /^[1](([3][0-9])|([4][0,1,4-9])|([5][0-3,5-9])|([6][2,5,6,7])|([7][0-8])|([8][0-9])|([9][0-3,5-9]))[0-9]{8}$/;
  return reg.test(phone);
};

/**
 * 判断字符串是否为邮箱
 * @param email 字符串
 */
export const isEmail = (email: string): boolean => {
  const reg = /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
  return reg.test(email);
};
